import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  REQUEST_ACCESS,
  GET_USERS,
  SEND_INVITE,
  SHOW_NOTIFY,
  NOTIFY_LEVEL,
  CHANGE_USER_ACCESS_LEVEL,
  REMOVE_USER,
  WS_NEW_USER,
  WS_DELETE_USER,
  WS_UPDATE_USER,
  RequestStatus,
} from 'constants';
import { Utils } from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import api from '@control-front-end/common/sagas/api';

/**
 * Создать модель пользователя
 */
export function* createUserModel(list, useSorting = true) {
  list = Array.isArray(list) ? list : [list];
  if (useSorting) list = Utils.sort(list, 'nick');
  const config = yield select((state) => state.config);
  return list.map((user) => {
    const loginObj =
      user.logins.find((i) => i.type === 'google') ||
      user.logins.find((i) => i.type === 'corezoid');
    const email = loginObj ? loginObj.login : '';
    return {
      id: user.id,
      value: user.nick,
      role: user.role,
      type: user.type,
      avatar: config.avatarPath.replace('{userId}', user.id),
      createdAt: user.createdAt,
      email,
    };
  });
}

/**
 * Группировка пользователей по типу
 */
const groupUsersByType = (list) => {
  const usersGroup = AppUtils.groupBy(list, 'type');
  const { api: apiUser = [], user = [] } = usersGroup;
  return user.concat(apiUser);
};

/**
 * Получить пользователей
 */
function* getUsers({ payload: accId }) {
  // Проверка авторизации
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/users/${accId}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const list = yield call(createUserModel, data.data);
  const userList = groupUsersByType(list);
  yield put({ type: GET_USERS.SUCCESS, payload: userList });
}

/**
 * Отправить приглашение пользователю
 */
function* sendInvite({ payload }) {
  const { invitations, newInvites, formActions } = payload;
  const accounts = yield select((state) => state.accounts);
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/invites/${accounts.active}`,
    body: invitations,
  });
  if (formActions) {
    formActions.setSubmitting(false);
  }
  if (result !== RequestStatus.SUCCESS) return;
  if (formActions) {
    formActions.onClose();
  }

  if (newInvites) {
    // проставляем id новым инвайтам
    newInvites.forEach((i) => {
      i.id = data.data.find((u) => u.email === i.email).id;
    });
    yield put({ type: SEND_INVITE.SUCCESS, payload: newInvites });
  }
  yield put({
    type: SHOW_NOTIFY.REQUEST,
    payload: {
      id: AppUtils.createRid(),
      type: NOTIFY_LEVEL.SUCCESS,
      label: 'The invitation has been sent',
    },
  });
}

/**
 * Запросить доступ к системе
 */
function* requestAccess({ payload }) {
  const { body, formActions } = payload;
  // Проверка авторизации
  const { result, data } = yield call(api, {
    method: 'post',
    url: '/users/permission_request',
    body,
  });
  formActions.setSubmitting(false);
  if (result !== RequestStatus.SUCCESS) return;
  yield put({ type: REQUEST_ACCESS.SUCCESS, payload: data.data });
  yield put({
    type: SHOW_NOTIFY.REQUEST,
    payload: {
      id: AppUtils.createRid(),
      type: NOTIFY_LEVEL.SUCCESS,
      label:
        'The request has been sent. On your email will be send invitation when admin will verify your data.',
    },
  });
}

/**
 * Изменить роль пользователя в аккаунте
 */
function* changeAccessLevel({ payload }) {
  const accounts = yield select((state) => state.accounts);
  const { result } = yield call(api, {
    method: 'put',
    url: `/users/role/${accounts.active}`,
    body: payload,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const userList = yield select((state) => state.users);
  const copyList = structuredClone(userList);
  const findUser = copyList.find((i) => i.id === payload.userId);
  findUser.role = payload.role;
  yield put({ type: CHANGE_USER_ACCESS_LEVEL.SUCCESS, payload: copyList });
}

/**
 * Удалить пользователя из аккаунтта
 */
function* removeUser({ payload }) {
  const accounts = yield select((state) => state.accounts);
  const { result } = yield call(api, {
    method: 'delete',
    url: `/users/${accounts.active}`,
    body: payload,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const userList = yield select((state) => state.users);
  const newUsers = userList.filter((i) => i.id !== payload.userId);
  yield put({ type: REMOVE_USER.SUCCESS, payload: newUsers });
}

/**
 * Новый пользователь по WS
 */
function* newUserWS({ payload }) {
  const accounts = yield select((state) => state.accounts);
  const userList = yield select((state) => state.users);
  const { accId, user } = payload;
  if (accounts.active !== accId) return;
  const userModel = yield call(createUserModel, user);
  const newList = userList.slice();
  newList.unshift(userModel[0]);
  yield put({ type: GET_USERS.SUCCESS, payload: newList });
}

/**
 * Удалить пользователя по WS
 */
function* deleteUserWS({ payload }) {
  const accounts = yield select((state) => state.accounts);
  const userList = yield select((state) => state.users);
  const { accId, user } = payload;
  if (accounts.active !== accId) return;
  const usersList = userList.filter((i) => i.id !== user.id);
  yield put({ type: GET_USERS.SUCCESS, payload: usersList });
}

/**
 * Удалить пользователя по WS
 */
function* updateUserWS({ payload }) {
  const accounts = yield select((state) => state.accounts);
  const userList = yield select((state) => state.users);
  const { accId, user } = payload;
  if (accounts.active !== accId) return;
  const copyList = structuredClone(userList);
  const index = copyList.findIndex((i) => i.id === user.id);
  if (index === -1) return;
  const userModel = yield call(createUserModel, user);
  copyList.splice(index, 1, userModel[0]);
  yield put({ type: GET_USERS.SUCCESS, payload: copyList });
}

function* users() {
  yield takeLatest(GET_USERS.REQUEST, getUsers);
  yield takeLatest(SEND_INVITE.REQUEST, sendInvite);
  yield takeLatest(REQUEST_ACCESS.REQUEST, requestAccess);
  yield takeLatest(CHANGE_USER_ACCESS_LEVEL.REQUEST, changeAccessLevel);
  yield takeLatest(REMOVE_USER.REQUEST, removeUser);
  yield takeLatest(WS_NEW_USER, newUserWS);
  yield takeLatest(WS_DELETE_USER, deleteUserWS);
  yield takeLatest(WS_UPDATE_USER, updateUserWS);
}

export default users;
